/**
 * Admin Dashboard
 * Page for admins to manage employees, settings, and view all data
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import EmployeeForm from '../components/EmployeeForm';
import RequestCard from '../components/RequestCard';
import PasswordChangeForm from '../components/PasswordChangeForm';
import LoadingSpinner from '../components/LoadingSpinner';
import employeeService from '../services/employeeService';
import settingsService from '../services/settingsService';
import centerService from '../services/centerService';
import logger from '../utils/logger';
import auditService from '../services/auditService';
import { formatDate } from '../utils/dateHelpers';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { employees, requests, settings, centers, refreshAll, loading: appLoading } = useApp();
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [activeTab, setActiveTab] = useState('employees');
  const [processing, setProcessing] = useState(false);
  const [passwordResetEmployee, setPasswordResetEmployee] = useState(null);
  const [showCenterForm, setShowCenterForm] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [centerForm, setCenterForm] = useState({ name: '', description: '', blackoutDays: [], yearlyTokenLimit: null });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showBlackoutModal, setShowBlackoutModal] = useState(false);
  const [blackoutModalCenter, setBlackoutModalCenter] = useState(null);
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  const [centerFormErrors, setCenterFormErrors] = useState({});
  
  useEffect(() => {
    logger.info('AdminDashboard mounted', { userId: user?.id });
    refreshAll();
  }, [user, refreshAll]);
  
  useEffect(() => {
    if (settings) {
      setSettingsForm(settings);
    }
  }, [settings]);
  
  const handleCreateEmployee = async (formData) => {
    setProcessing(true);
    try {
      logger.info('Creating employee', { formData, userId: user.id });

      // Check for duplicate email (case-insensitive)
      const duplicateEmail = employees.find(
        emp => emp.email.toLowerCase() === formData.email.toLowerCase()
      );
      if (duplicateEmail) {
        alert(`An employee with email "${duplicateEmail.email}" already exists. Email addresses must be unique (case-insensitive).`);
        setProcessing(false);
        return;
      }

      const newEmployee = await employeeService.createEmployee(formData);

      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'create',
        entityType: 'employee',
        entityId: newEmployee.id,
        details: formData,
      });

      await refreshAll();
      setShowEmployeeForm(false);
      logger.info('Employee created successfully', { employeeId: newEmployee.id });
    } catch (error) {
      logger.error('Error creating employee', error, { userId: user.id });
      
      let errorMessage = error?.message || 'Unknown error';

      if (error.code === 'SUPABASE_CONFIG_MISSING') {
        errorMessage = 'Supabase configuration is missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your environment.';
      } else if (error.status === 401 || error.status === 403) {
        errorMessage = 'Supabase rejected the request (status ' + error.status + '). Verify that your anon key is correct and that Row Level Security policies allow this operation.';
      }

      alert('Failed to create employee:\n\n' + errorMessage);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleUpdateEmployee = async (formData) => {
    setProcessing(true);
    try {
      logger.info('Updating employee', { employeeId: editingEmployee.id, formData, userId: user.id });
      
      // Check for duplicate email (case-insensitive), excluding the current employee
      const duplicateEmail = employees.find(
        emp => emp.id !== editingEmployee.id && 
               emp.email.toLowerCase() === formData.email.toLowerCase()
      );
      if (duplicateEmail) {
        alert(`An employee with email "${duplicateEmail.email}" already exists. Email addresses must be unique (case-insensitive).`);
        setProcessing(false);
        return;
      }
      
      // Remove password field if empty (don't update password on edit)
      const updateData = { ...formData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      }
      
      const updatedEmployee = await employeeService.updateEmployee(editingEmployee.id, updateData);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'update',
        entityType: 'employee',
        entityId: updatedEmployee.id,
        details: formData,
      });
      
      await refreshAll();
      setEditingEmployee(null);
      setShowEmployeeForm(false);
      logger.info('Employee updated successfully', { employeeId: updatedEmployee.id });
    } catch (error) {
      logger.error('Error updating employee', error, { userId: user.id });
      alert('Failed to update employee: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }
    
    setProcessing(true);
    try {
      logger.info('Deleting employee', { employeeId, userId: user.id });
      
      await employeeService.deleteEmployee(employeeId);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'delete',
        entityType: 'employee',
        entityId: employeeId,
      });
      
      await refreshAll();
      logger.info('Employee deleted successfully', { employeeId });
    } catch (error) {
      logger.error('Error deleting employee', error, { userId: user.id });
      alert('Failed to delete employee: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleUpdateSettings = async () => {
    setProcessing(true);
    try {
      logger.info('Updating settings', { settingsForm, userId: user.id });
      
      await settingsService.updateSettings(settingsForm);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'update',
        entityType: 'settings',
        details: settingsForm,
      });
      
      await refreshAll();
      alert('Settings updated successfully');
      logger.info('Settings updated successfully');
    } catch (error) {
      logger.error('Error updating settings', error, { userId: user.id });
      alert('Failed to update settings: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCreateCenter = async (formData) => {
    setProcessing(true);
    try {
      logger.info('Creating center', { formData, userId: user.id });
      
      // Validate yearlyTokenLimit
      const systemLimit = settings.yearlyTokenLimit || 12;
      if (formData.yearlyTokenLimit !== null && formData.yearlyTokenLimit !== undefined && formData.yearlyTokenLimit !== '') {
        const limit = parseInt(formData.yearlyTokenLimit);
        if (limit > systemLimit) {
          alert(`Center yearly token limit (${limit}) cannot exceed system settings limit (${systemLimit})`);
          setProcessing(false);
          return;
        }
      }
      
      const newCenter = await centerService.createCenter(formData);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'create',
        entityType: 'center',
        entityId: newCenter.id,
        details: formData,
      });
      
      await refreshAll();
      setShowCenterForm(false);
      setCenterForm({ name: '', description: '', blackoutDays: [], yearlyTokenLimit: null });
      setCenterFormErrors({});
      alert('Center created successfully');
      logger.info('Center created successfully', { centerId: newCenter.id });
    } catch (error) {
      logger.error('Error creating center', error, { userId: user.id });
      alert('Failed to create center: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleUpdateCenter = async (centerId, formData) => {
    setProcessing(true);
    try {
      logger.info('Updating center', { centerId, formData, userId: user.id });
      
      // Validate yearlyTokenLimit
      const systemLimit = settings.yearlyTokenLimit || 12;
      if (formData.yearlyTokenLimit !== null && formData.yearlyTokenLimit !== undefined && formData.yearlyTokenLimit !== '') {
        const limit = parseInt(formData.yearlyTokenLimit);
        if (limit > systemLimit) {
          alert(`Center yearly token limit (${limit}) cannot exceed system settings limit (${systemLimit})`);
          setProcessing(false);
          return;
        }
      }
      
      await centerService.updateCenter(centerId, formData);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'update',
        entityType: 'center',
        entityId: centerId,
        details: formData,
      });
      
      await refreshAll();
      setEditingCenter(null);
      setCenterForm({ name: '', description: '', blackoutDays: [], yearlyTokenLimit: null });
      setCenterFormErrors({});
      alert('Center updated successfully');
      logger.info('Center updated successfully', { centerId });
    } catch (error) {
      logger.error('Error updating center', error, { centerId, userId: user.id });
      alert('Failed to update center: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDeleteCenter = async (centerId) => {
    if (!window.confirm('Are you sure you want to delete this center? Employees assigned to this center will have their centerId set to null.')) {
      return;
    }
    
    setProcessing(true);
    try {
      logger.info('Deleting center', { centerId, userId: user.id });
      
      // Remove center assignment from employees
      const employeesWithCenter = employees.filter(emp => emp.centerId === centerId);
      for (const emp of employeesWithCenter) {
        await employeeService.updateEmployee(emp.id, { centerId: null });
      }
      
      await centerService.deleteCenter(centerId);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'delete',
        entityType: 'center',
        entityId: centerId,
        details: { affectedEmployees: employeesWithCenter.length },
      });
      
      await refreshAll();
      alert('Center deleted successfully');
      logger.info('Center deleted successfully', { centerId });
    } catch (error) {
      logger.error('Error deleting center', error, { centerId, userId: user.id });
      alert('Failed to delete center: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleOpenBlackoutModal = (center) => {
    setBlackoutModalCenter(center);
    setShowBlackoutModal(true);
    setNewBlackoutDate('');
  };
  
  const handleAddBlackoutDay = async (centerId, date) => {
    setProcessing(true);
    try {
      await centerService.addBlackoutDay(centerId, date);
      await refreshAll();
      // Update the modal center data
      const updatedCenter = await centerService.getCenterById(centerId);
      setBlackoutModalCenter(updatedCenter);
      setNewBlackoutDate('');
    } catch (error) {
      logger.error('Error adding blackout day', error, { centerId, date });
      alert('Failed to add blackout day: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleRemoveBlackoutDay = async (centerId, date) => {
    setProcessing(true);
    try {
      await centerService.removeBlackoutDay(centerId, date);
      await refreshAll();
      // Update the modal center data
      const updatedCenter = await centerService.getCenterById(centerId);
      setBlackoutModalCenter(updatedCenter);
    } catch (error) {
      logger.error('Error removing blackout day', error, { centerId, date });
      alert('Failed to remove blackout day: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  if (appLoading && !processing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {processing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <LoadingSpinner size="lg" text="Processing request..." />
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-primary-500 mb-2">
            Admin Dashboard
          </h1>
          <p className="text-gray-600 text-lg">Manage employees, settings, and innovation request platform</p>
          <div className="mt-4 flex items-center gap-4">
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2 bg-white border border-primary-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>{showPasswordForm ? 'Cancel' : 'Change Password'}</span>
            </button>
          </div>
        </div>
        
        {/* Password Change Form */}
        {showPasswordForm && (
          <div className="bg-white rounded-2xl shadow-soft p-8 mb-6 border border-gray-100 animate-slide-up">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Change Your Password</h3>
            <PasswordChangeForm
              onSuccess={() => {
                setShowPasswordForm(false);
                alert('Password changed successfully!');
              }}
              onCancel={() => setShowPasswordForm(false)}
            />
          </div>
        )}
        
        {/* Tabs */}
        <div className="mb-6 bg-white rounded-2xl shadow-soft p-2 border border-gray-100">
          <nav className="flex space-x-2">
            {['employees', 'centers', 'requests', 'settings'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-primary-500 text-white shadow-lg hover:bg-primary-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="glass p-8 animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Employees</h2>
              </div>
              <button
                onClick={() => {
                  setEditingEmployee(null);
                  setShowEmployeeForm(!showEmployeeForm);
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 ${
                  showEmployeeForm
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-primary-500 text-white shadow-lg hover:shadow-xl hover:bg-primary-600'
                }`}
              >
                {showEmployeeForm ? (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Employee</span>
                  </span>
                )}
              </button>
            </div>
            
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-primary-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Center/Team
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map(employee => (
                    <tr key={employee.id} className="hover:bg-primary-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            {employee.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{employee.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {employee.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          employee.role === 'admin' ? 'bg-primary-600 text-white' :
                          employee.role === 'manager' ? 'bg-primary-500 text-white' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {employee.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {employee.centerId ? (
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg font-medium">
                            {centers.find(c => c.id === employee.centerId)?.name || 'Unknown Center'}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">No center</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                        <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-lg">
                          {employee.tokensUsed || 0} / {employee.yearlyTokens || 12}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => {
                              setEditingEmployee(employee);
                              setShowEmployeeForm(true);
                            }}
                            className="px-4 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setPasswordResetEmployee(employee);
                              setShowEmployeeForm(true);
                            }}
                            className="px-4 py-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-lg transition-all duration-200 font-semibold"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={() => handleDeleteEmployee(employee.id)}
                            className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Centers Tab */}
        {activeTab === 'centers' && (
          <div className="glass p-8 animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Centers/Teams</h2>
              </div>
              <button
                onClick={() => {
                  setEditingCenter(null);
                  setCenterForm({ name: '', description: '', blackoutDays: [], yearlyTokenLimit: '' });
                  setCenterFormErrors({});
                  setShowCenterForm(!showCenterForm);
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 ${
                  showCenterForm
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-blue-500 text-white shadow-lg hover:shadow-xl hover:bg-blue-600'
                }`}
              >
                {showCenterForm ? (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Center</span>
                  </span>
                )}
              </button>
            </div>
            
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              {centers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg font-medium mb-2">No centers configured</p>
                  <p className="text-gray-400 text-sm">Click "Add Center" to create your first center/team</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Yearly Token Limit
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Blackout Days
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Employees
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {centers.map(center => {
                      const centerEmployees = employees.filter(emp => emp.centerId === center.id);
                      const systemLimit = settings.yearlyTokenLimit || 12;
                      const effectiveLimit = center.yearlyTokenLimit !== null && center.yearlyTokenLimit !== undefined 
                        ? center.yearlyTokenLimit 
                        : systemLimit;
                      return (
                        <tr key={center.id} className="hover:bg-blue-50 transition-colors duration-150">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                {center.name?.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{center.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {center.description || <span className="text-gray-400 italic">No description</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-lg font-medium text-sm w-fit">
                                {effectiveLimit} tokens
                              </span>
                              {(center.yearlyTokenLimit === null || center.yearlyTokenLimit === undefined) && (
                                <span className="text-xs text-gray-500 mt-1">
                                  (System default)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-2">
                              {center.blackoutDays && center.blackoutDays.length > 0 ? (
                                center.blackoutDays.map((date, idx) => (
                                  <span
                                    key={idx}
                                    className="px-3 py-1 bg-red-50 text-red-700 rounded-lg font-medium text-xs"
                                  >
                                    {date}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-400 italic text-xs">No blackout days</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-lg font-medium text-sm">
                              {centerEmployees.length} {centerEmployees.length === 1 ? 'employee' : 'employees'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => {
                                  setEditingCenter(center);
                                  setCenterForm({
                                    name: center.name,
                                    description: center.description || '',
                                    blackoutDays: center.blackoutDays || [],
                                    yearlyTokenLimit: center.yearlyTokenLimit !== null && center.yearlyTokenLimit !== undefined 
                                      ? center.yearlyTokenLimit 
                                      : '',
                                  });
                                  setShowCenterForm(true);
                                }}
                                className="px-4 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 font-semibold"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleOpenBlackoutModal(center)}
                                className="px-4 py-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-all duration-200 font-semibold"
                              >
                                Manage Blackouts
                              </button>
                              <button
                                onClick={() => handleDeleteCenter(center.id)}
                                className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 font-semibold"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
        
        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="glass p-8 animate-slide-up">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">All Requests</h2>
              </div>
              <div className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg font-semibold text-sm">
                {requests.length} {requests.length === 1 ? 'Request' : 'Requests'}
              </div>
            </div>
            <div className="space-y-4">
              {requests.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg font-medium">No requests yet</p>
                </div>
              ) : (
                requests
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map(request => {
                    const employee = employees.find(emp => emp.id === request.employeeId);
                    return (
                      <RequestCard
                        key={request.id}
                        request={request}
                        employee={employee}
                        employees={employees}
                        onView={(req) => setSelectedRequest(req)}
                      />
                    );
                  })
              )}
            </div>
          </div>
        )}
        
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="glass p-8 animate-slide-up">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">System Settings</h2>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Yearly Token Limit (default)
                </label>
                <input
                  type="number"
                  value={settingsForm.yearlyTokenLimit || 12}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      yearlyTokenLimit: parseInt(e.target.value) || 12,
                    })
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                  min="0"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Max Consecutive Days
                </label>
                <input
                  type="number"
                  value={settingsForm.maxConsecutiveDays || 3}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      maxConsecutiveDays: parseInt(e.target.value) || 3,
                    })
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                  min="1"
                  max="3"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Minimum Advance Notice (Days)
                </label>
                <input
                  type="number"
                  value={settingsForm.minAdvanceNoticeDays !== undefined ? settingsForm.minAdvanceNoticeDays : 14}
                  onChange={(e) =>
                    setSettingsForm({
                      ...settingsForm,
                      minAdvanceNoticeDays: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                  min="0"
                  placeholder="14"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum number of days in advance that requests must be submitted. Set to 0 to disable this requirement.
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center justify-between">
                  <span className="block text-sm font-semibold text-gray-700">
                    Email Notifications
                  </span>
                  <div className="relative inline-block w-14 h-8">
                    <input
                      type="checkbox"
                      checked={settingsForm.emailNotificationsEnabled !== false}
                      onChange={(e) =>
                        setSettingsForm({
                          ...settingsForm,
                          emailNotificationsEnabled: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-14 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all peer-checked:bg-primary-500"></div>
                  </div>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Enable or disable email notifications for request approvals, rejections, cancellations, and other events. 
                  Note: You can also disable emails globally via REACT_APP_EMAIL_NOTIFICATIONS_ENABLED=false in .env file.
                </p>
              </div>
              
              <button
                onClick={handleUpdateSettings}
                className="w-full px-6 py-3.5 bg-primary-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:bg-primary-600 transform hover:scale-[1.02] transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-300 flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Save Settings</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Request Details Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
              <div className="sticky top-0 bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 flex items-center justify-between border-b border-primary-400">
                <h2 className="text-2xl font-bold text-white">Request Details</h2>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-white hover:bg-primary-700 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Employee Info */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-md">
                    {employees.find(emp => emp.id === selectedRequest.employeeId)?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {employees.find(emp => emp.id === selectedRequest.employeeId)?.name || 'Unknown Employee'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {employees.find(emp => emp.id === selectedRequest.employeeId)?.email || ''}
                    </p>
                  </div>
                </div>
                
                {/* Manager Info */}
                {(() => {
                  const employee = employees.find(emp => emp.id === selectedRequest.employeeId);
                  const manager = employee?.managerId ? employees.find(emp => emp.id === employee.managerId) : null;
                  
                  return (
                    <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex-shrink-0">
                        <svg className="w-12 h-12 text-blue-600 bg-blue-100 rounded-lg p-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-1">Request Raised To</p>
                        {manager ? (
                          <div>
                            <h3 className="text-lg font-bold text-blue-900">{manager.name}</h3>
                            <p className="text-sm text-blue-700">{manager.email}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-blue-600 italic">No manager assigned</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Status</p>
                    <p className={`text-lg font-bold px-3 py-1 rounded-lg inline-block ${
                      selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedRequest.status === 'cancellation_requested' ? 'bg-orange-100 text-orange-800' :
                      selectedRequest.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedRequest.status.replace(/_/g, ' ').toUpperCase()}
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Duration</p>
                    <p className="text-lg font-bold text-primary-600">
                      {selectedRequest.days || 1} {selectedRequest.days === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                </div>
                
                {/* Dates */}
                <div className="p-4 border border-gray-200 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Dates</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">Start Date:</span>
                      <span className="text-gray-800 font-semibold">{formatDate(selectedRequest.startDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">End Date:</span>
                      <span className="text-gray-800 font-semibold">{formatDate(selectedRequest.endDate)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Reason */}
                {selectedRequest.reason && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">Reason</p>
                    <p className="text-gray-800">{selectedRequest.reason}</p>
                  </div>
                )}
                
                {/* Rejection Reason */}
                {selectedRequest.status === 'rejected' && selectedRequest.rejectionReason && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-red-600 font-semibold mb-2">Rejection Reason</p>
                    <p className="text-gray-800">{selectedRequest.rejectionReason}</p>
                  </div>
                )}
                
                {/* Cancellation Reason */}
                {selectedRequest.cancellationReason && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold mb-2">Cancellation Reason</p>
                    <p className="text-gray-800">{selectedRequest.cancellationReason}</p>
                  </div>
                )}
                
                {/* Review Info */}
                {selectedRequest.reviewedAt && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Reviewed</p>
                    <p className="text-gray-800">
                      <span className="font-semibold">{formatDate(selectedRequest.reviewedAt)}</span> by {employees.find(emp => emp.id === selectedRequest.reviewedBy)?.name || selectedRequest.reviewedBy || 'Admin'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="px-6 py-2.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Employee Form Modal */}
        {showEmployeeForm && !passwordResetEmployee && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 flex items-center justify-between border-b border-primary-400">
                <h2 className="text-2xl font-bold text-white">
                  {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                </h2>
                <button
                  onClick={() => {
                    setShowEmployeeForm(false);
                    setEditingEmployee(null);
                  }}
                  className="text-white hover:bg-primary-700 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <EmployeeForm
                  employee={editingEmployee}
                  employees={employees}
                  centers={centers}
                  onSubmit={editingEmployee ? handleUpdateEmployee : handleCreateEmployee}
                  onCancel={() => {
                    setShowEmployeeForm(false);
                    setEditingEmployee(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Password Reset Modal */}
        {passwordResetEmployee && showEmployeeForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-amber-500 px-6 py-4 flex items-center justify-between border-b border-yellow-400">
                <h2 className="text-2xl font-bold text-white">
                  Reset Password
                </h2>
                <button
                  onClick={() => {
                    setPasswordResetEmployee(null);
                    setShowEmployeeForm(false);
                  }}
                  className="text-white hover:bg-yellow-600 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-gray-700">
                    Resetting password for: <span className="font-bold text-gray-900">{passwordResetEmployee.name}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{passwordResetEmployee.email}</p>
                </div>
                
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const password = e.target.newPassword.value;
                    if (!password || password.length < 6) {
                      alert('Password must be at least 6 characters long');
                      return;
                    }
                    setProcessing(true);
                    try {
                      await employeeService.resetPassword(passwordResetEmployee.id, password);
                      await auditService.logAudit({
                        userId: user.id,
                        userRole: user.role,
                        action: 'reset',
                        entityType: 'password',
                        entityId: passwordResetEmployee.id,
                        details: { employeeEmail: passwordResetEmployee.email },
                      });
                      await refreshAll();
                      setPasswordResetEmployee(null);
                      setShowEmployeeForm(false);
                      alert('Password reset successfully!');
                    } catch (error) {
                      alert('Failed to reset password: ' + error.message);
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                      New Password *
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      placeholder="Enter new password (min 6 characters)"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={processing}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                    >
                      {processing ? 'Resetting...' : 'Reset Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPasswordResetEmployee(null);
                        setShowEmployeeForm(false);
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        {/* Center Form Modal */}
        {showCenterForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4 flex items-center justify-between border-b border-blue-400">
                <h2 className="text-2xl font-bold text-white">
                  {editingCenter ? 'Edit Center' : 'Create New Center'}
                </h2>
                <button
                  onClick={() => {
                    setShowCenterForm(false);
                    setEditingCenter(null);
                    setCenterForm({ name: '', description: '', blackoutDays: [] });
                  }}
                  className="text-white hover:bg-blue-600 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Center Name *
                    </label>
                    <input
                      type="text"
                      value={centerForm.name}
                      onChange={(e) => setCenterForm({ ...centerForm, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                      placeholder="e.g., Chennai Team"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={centerForm.description}
                      onChange={(e) => setCenterForm({ ...centerForm, description: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-gray-50 focus:bg-white"
                      placeholder="e.g., Chennai development center"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Yearly Token Limit
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={centerForm.yearlyTokenLimit}
                        onChange={(e) => {
                          const value = e.target.value;
                          const systemLimit = settings.yearlyTokenLimit || 12;
                          setCenterForm({ ...centerForm, yearlyTokenLimit: value });
                          
                          // Real-time validation
                          if (value !== '' && parseInt(value) > systemLimit) {
                            setCenterFormErrors({ 
                              ...centerFormErrors, 
                              yearlyTokenLimit: `Cannot exceed system limit of ${systemLimit}` 
                            });
                          } else {
                            const { yearlyTokenLimit, ...restErrors } = centerFormErrors;
                            setCenterFormErrors(restErrors);
                          }
                        }}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-gray-50 focus:bg-white ${
                          centerFormErrors.yearlyTokenLimit 
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-200' 
                            : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                        }`}
                        placeholder={`Leave empty to use system default (${settings.yearlyTokenLimit || 12})`}
                        min="0"
                        max={settings.yearlyTokenLimit || 12}
                      />
                    </div>
                    {centerFormErrors.yearlyTokenLimit && (
                      <p className="mt-1 text-sm text-red-600">{centerFormErrors.yearlyTokenLimit}</p>
                    )}
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-xs text-blue-800">
                          <p className="font-semibold mb-1">System Yearly Token Limit: {settings.yearlyTokenLimit || 12}</p>
                          <p className="text-blue-700">
                            • Leave empty to inherit system default<br />
                            • Can be decreased below system limit<br />
                            • Cannot exceed system limit<br />
                            • This limit applies to all employees/managers assigned to this center
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={() => {
                        if (editingCenter) {
                          // Convert empty string to null before submitting
                          const submitData = {
                            ...centerForm,
                            yearlyTokenLimit: centerForm.yearlyTokenLimit === '' ? null : parseInt(centerForm.yearlyTokenLimit)
                          };
                          handleUpdateCenter(editingCenter.id, submitData);
                        } else {
                          // Convert empty string to null before submitting
                          const submitData = {
                            ...centerForm,
                            yearlyTokenLimit: centerForm.yearlyTokenLimit === '' ? null : parseInt(centerForm.yearlyTokenLimit)
                          };
                          handleCreateCenter(submitData);
                        }
                      }}
                      disabled={!centerForm.name || processing || !!centerFormErrors.yearlyTokenLimit}
                      className="flex-1 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:bg-blue-600 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingCenter ? 'Update Center' : 'Create Center'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCenterForm(false);
                        setEditingCenter(null);
                        setCenterForm({ name: '', description: '', blackoutDays: [], yearlyTokenLimit: '' });
                        setCenterFormErrors({});
                      }}
                      className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Blackout Days Management Modal */}
        {showBlackoutModal && blackoutModalCenter && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 flex items-center justify-between border-b border-orange-400">
                <div>
                  <h2 className="text-2xl font-bold text-white">Manage Blackout Days</h2>
                  <p className="text-orange-100 text-sm mt-1">{blackoutModalCenter.name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowBlackoutModal(false);
                    setBlackoutModalCenter(null);
                    setNewBlackoutDate('');
                  }}
                  className="text-white hover:bg-orange-600 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Add New Blackout Day - Fixed at top */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 border-b-2 border-orange-200 p-5 flex-shrink-0">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Add Blackout Day</span>
                </h3>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="date"
                      value={newBlackoutDate}
                      onChange={(e) => setNewBlackoutDate(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (newBlackoutDate) {
                        if (blackoutModalCenter.blackoutDays?.includes(newBlackoutDate)) {
                          alert('This date is already in the blackout list');
                          return;
                        }
                        
                        // Check if selected date is a weekend (Saturday = 6, Sunday = 0)
                        const dateObj = new Date(newBlackoutDate + 'T00:00:00');
                        const dayOfWeek = dateObj.getDay();
                        if (dayOfWeek === 0 || dayOfWeek === 6) {
                          alert('Weekends (Saturday and Sunday) are already holidays by default and cannot be added as blackout days');
                          return;
                        }
                        
                        handleAddBlackoutDay(blackoutModalCenter.id, newBlackoutDate);
                      } else {
                        alert('Please select a date');
                      }
                    }}
                    disabled={!newBlackoutDate || processing}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 whitespace-nowrap"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Date</span>
                  </button>
                </div>
                <p className="text-xs text-orange-700 mt-2 flex items-start space-x-1">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Blackout days prevent employees from submitting innovation requests on these dates</span>
                </p>
              </div>
              
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Current Blackout Days */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center justify-between">
                    <span>Current Blackout Days</span>
                    <span className="text-sm font-semibold px-3 py-1 bg-gray-100 text-gray-700 rounded-lg">
                      {blackoutModalCenter.blackoutDays?.length || 0} {blackoutModalCenter.blackoutDays?.length === 1 ? 'day' : 'days'}
                    </span>
                  </h3>
                  
                  {!blackoutModalCenter.blackoutDays || blackoutModalCenter.blackoutDays.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-lg font-medium mb-1">No blackout days configured</p>
                      <p className="text-gray-400 text-sm">Add dates above to create blackout days for this center</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[...blackoutModalCenter.blackoutDays].sort().map((date, idx) => {
                        const dateObj = new Date(date + 'T00:00:00');
                        const isPast = dateObj < new Date(new Date().setHours(0, 0, 0, 0));
                        const formattedDate = dateObj.toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        });
                        
                        return (
                          <div
                            key={idx}
                            className={`group relative p-4 rounded-xl border-2 transition-all duration-200 ${
                              isPast
                                ? 'bg-gray-50 border-gray-200 opacity-60'
                                : 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <svg className={`w-4 h-4 flex-shrink-0 ${
                                    isPast ? 'text-gray-400' : 'text-red-500'
                                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <p className={`text-sm font-bold ${
                                    isPast ? 'text-gray-600' : 'text-red-900'
                                  }`}>
                                    {formattedDate}
                                  </p>
                                </div>
                                <p className={`text-xs ${
                                  isPast ? 'text-gray-500' : 'text-red-600'
                                }`}>
                                  {date}
                                </p>
                                {isPast && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-md font-medium">
                                    Past
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Remove blackout day ${date}?`)) {
                                    handleRemoveBlackoutDay(blackoutModalCenter.id, date);
                                  }
                                }}
                                disabled={processing}
                                className={`p-1.5 rounded-lg transition-all duration-200 ${
                                  isPast
                                    ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                                    : 'text-red-600 hover:text-red-700 hover:bg-red-100'
                                } disabled:opacity-50`}
                                title="Remove blackout day"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
                <button
                  onClick={() => {
                    setShowBlackoutModal(false);
                    setBlackoutModalCenter(null);
                    setNewBlackoutDate('');
                  }}
                  className="px-6 py-2.5 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

